/*
 * Ring Design Product — storefront add-to-cart for ready-made design products.
 *
 * Renders price + size selector for a published design product, and on add-to-cart
 * ROUTES to the ring-designer base variant SS-{width}mm-Plain-{size}-T{tier} with
 * the design's line-item properties attached — so the order is indistinguishable
 * from a live-designer order (admin can open/modify/export; Craftybase pulls base SKU).
 *
 * Config is injected per-product as window.RDP[productId] by the liquid snippet:
 *   { productId, variantMap:{tier:{size:{id,price,compareAt,available}}}, pricesBySize,
 *     tierSlug, width, popularId, designHash, currencySymbol }
 *
 * The design product's own Size variants (SKU RD-{DESIGN}-{w}MM-{size}) are catalog
 * only — never added to the cart; we always add the base variant. A ?variant= link
 * preselects that size.
 */
(function () {
  'use strict';

  // Storefront sizes (must match the ring designer / popular designs list).
  var SIZES = ['4','4.5','5','5.5','6','6.5','7','7.5','8','8.5','9','9.5','10','10.5','11','11.5','12','12.5','13','13.5','14','14.5','15'];

  function fmt(cents) {
    if (cents == null || isNaN(cents)) return '';
    return '$' + (cents / 100).toFixed(2);
  }

  // Resolve the base variant for a given size. Tier is locked per design (cfg.tierSlug),
  // but honor a per-size tier from pricesBySize if present (data model allows it).
  function resolveBase(cfg, size) {
    var tierSlug = cfg.tierSlug;
    var row = cfg.pricesBySize && (cfg.pricesBySize[String(size)] || cfg.pricesBySize[String(size).replace('.0', '')]);
    if (row && row.tierSlug) tierSlug = row.tierSlug;
    var wKey = String(cfg.width || 8).replace('mm', '');
    // Primary: live variant map built from the base product in Liquid — the
    // 8mm base product ONLY; other widths must use the width-keyed server map.
    if (wKey === '8' && cfg.variantMap && tierSlug) {
      var tierMap = cfg.variantMap[tierSlug];
      if (tierMap) {
        var v = tierMap[String(size)] || tierMap[String(size).replace('.0', '')];
        if (v) return { variantId: v.id, price: v.price, compareAt: v.compareAt, available: v.available, tierSlug: tierSlug };
      }
    }
    // Server-side map: the ONLY complete source. Liquid loops and the public
    // product JSON both truncate at 250 variants (the base product has 391),
    // which broke sizes ≥ ~11 — so this covers everything the above missed.
    var srvTiers = cfg.serverRec
      ? ((cfg.serverRec.widths && cfg.serverRec.widths[wKey]) || (wKey === '8' ? cfg.serverRec.tiers : null))
      : (wKey === '8' ? cfg.serverMap : null);
    if (srvTiers && tierSlug) {
      var stm = srvTiers[tierSlug];
      if (stm) {
        var sv = stm[String(size)] || stm[String(size).replace('.0', '')];
        if (sv) return { variantId: sv.id, price: sv.price, compareAt: sv.compareAt, available: true, tierSlug: tierSlug };
      }
    }
    // Last resort: the per-size snapshot captured at publish (variantId + tier
    // price; no compare-at, so the strikethrough just doesn't show).
    if (row && row.variantId) {
      return { variantId: row.variantId, price: row.price, compareAt: null, available: true, tierSlug: tierSlug };
    }
    return null;
  }

  function renderPrice(cfg, priceEl, size) {
    var base = resolveBase(cfg, size);
    var cur = priceEl.querySelector('.rdp-price-current');
    var cmp = priceEl.querySelector('.rdp-price-compare');
    var badge = priceEl.querySelector('.rdp-price-badge');
    if (!base) { cur.textContent = ''; cmp.textContent = ''; badge.textContent = ''; return; }
    cur.textContent = fmt(base.price);
    if (base.compareAt && base.compareAt > base.price) {
      cmp.textContent = fmt(base.compareAt);
      var pct = Math.round((1 - base.price / base.compareAt) * 100);
      badge.textContent = pct > 0 ? ('Save ' + pct + '%') : '';
    } else {
      cmp.textContent = '';
      badge.textContent = '';
    }
  }

  // Build the {tier: {size: variant}} map from the public /products/<handle>.js
  // JSON — the deterministic variant source. Handles either option order.
  function buildMapFromProductJson(p) {
    var tierIdx = -1, sizeIdx = -1;
    (p.options || []).forEach(function (o, i) {
      var n = String(o && o.name ? o.name : o).toLowerCase();
      if (n.indexOf('tier') >= 0) tierIdx = i;
      else if (n.indexOf('size') >= 0) sizeIdx = i;
    });
    if (tierIdx < 0 || sizeIdx < 0) return null;
    var map = {};
    (p.variants || []).forEach(function (v) {
      var opts = v.options || [v.option1, v.option2];
      var tier = String(opts[tierIdx] || '').toLowerCase();
      var size = String(opts[sizeIdx] || '');
      if (!tier || !size) return;
      if (!map[tier]) map[tier] = {};
      map[tier][size] = { id: v.id, price: v.price, compareAt: v.compare_at_price || 0, available: v.available !== false };
    });
    return map;
  }

  function init(root) {
    var pid = root.getAttribute('data-product-id');
    var cfg = (window.RDP || {})[pid] || (window.RDP || {})[Number(pid)];
    if (!cfg) { console.warn('[RDP] no config for product', pid); return; }

    var sizeSel = root.querySelector('.rdp-size-select');
    var priceEl = root.querySelector('.rdp-price');
    var addBtn = root.querySelector('.rdp-add-btn');
    var msgEl = root.querySelector('.rdp-msg');

    // Populate size options — ALL sizes always available (options are king; physical
    // review happens before fulfillment). Mark the design's authored size as default.
    var defaultSize = (cfg.designHash && /(?:^|&)sz=([^&]+)/.exec(decodeURIComponent(cfg.designHash)) || [])[1] || '8';
    // A ?variant= link (Google Shopping, shared links) opens on that Size variant.
    var own = cfg.ownVariants || {};
    var linked = own[new URLSearchParams(window.location.search).get('variant') || ''];
    if (linked && SIZES.indexOf(String(linked)) >= 0) defaultSize = String(linked);
    function syncVariantParam(size) {
      var id = null;
      for (var k in own) if (String(own[k]) === String(size)) { id = k; break; }
      if (!id || !window.history || !window.history.replaceState) return;
      var u = new URL(window.location.href);
      u.searchParams.set('variant', id);
      window.history.replaceState(null, '', u.toString());
    }
    sizeSel.innerHTML = SIZES.map(function (s) {
      return '<option value="' + s + '"' + (s === defaultSize ? ' selected' : '') + '>US ' + s + '</option>';
    }).join('');

    // "Customize This Design" — opens the master design in the Ring Designer as
    // a starting point (same hash mechanism as share links / Popular Designs).
    // The chosen size is pushed into the hash (sz param) so the designer opens
    // at the size the customer already picked.
    var customize = root.querySelector('.rdp-customize-link');
    function updateCustomize(size) {
      if (!customize || !cfg.designHash) return;
      var sp = new URLSearchParams(cfg.designHash.replace(/^#/, ''));
      sp.set('sz', String(size));
      customize.href = (cfg.designerUrl || '/pages/inlay-ring-designer') + '#' + sp.toString();
      customize.style.display = 'inline-block';
    }

    // Gallery: thumbnail click swaps the main image.
    var mainImg = root.querySelector('#rdp-main-img');
    root.querySelectorAll('.rdp-thumb').forEach(function (t) {
      t.addEventListener('click', function () {
        if (mainImg && t.dataset.full) mainImg.src = t.dataset.full;
        root.querySelectorAll('.rdp-thumb').forEach(function (o) { o.classList.remove('rdp-thumb-active'); });
        t.classList.add('rdp-thumb-active');
      });
    });

    renderPrice(cfg, priceEl, sizeSel.value);
    updateCustomize(sizeSel.value);
    sizeSel.addEventListener('change', function () {
      renderPrice(cfg, priceEl, sizeSel.value);
      updateCustomize(sizeSel.value);
      syncVariantParam(sizeSel.value);
    });

    // ALWAYS fetch the server-side variant map — the only source with all 391
    // variants (Liquid loops and /products/<handle>.js truncate at 250, which
    // silently broke sizes ≥ ~11). Re-renders price when it lands.
    fetch('https://brs-ring-designer.netlify.app/.netlify/functions/variant-map')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (rec) {
        if (rec && (rec.tiers || rec.widths)) {
          cfg.serverRec = rec;
          cfg.serverMap = rec.tiers || null;
          renderPrice(cfg, priceEl, sizeSel.value);
        }
      })
      .catch(function (e) { console.warn('[RDP] variant-map fetch failed:', e.message); });

    // Backup (8mm only — the handle is the 8mm base): public product JSON
    // covers the first 250 variants for instant price paint.
    if (String(cfg.width || 8) === '8' && !cfg.variantMap && cfg.designerHandle) {
      fetch('/products/' + cfg.designerHandle + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) return;
          var map = buildMapFromProductJson(p);
          if (map) {
            cfg.variantMap = map;
            renderPrice(cfg, priceEl, sizeSel.value);
          }
        })
        .catch(function (e) { console.warn('[RDP] base product fetch failed:', e.message); });
    }

    addBtn.addEventListener('click', function () {
      var size = sizeSel.value;
      var base = resolveBase(cfg, size);
      if (!base || !base.variantId) {
        msgEl.textContent = 'Sorry — this size is temporarily unavailable. Please contact us.';
        msgEl.className = 'rdp-msg rdp-msg-err';
        return;
      }

      addBtn.disabled = true;
      var orig = addBtn.textContent;
      addBtn.textContent = 'Adding…';

      // Reconstruct the design URL: designer page + design hash. The hash rebuilds
      // the full design; Size is also sent as a line-item property (authoritative
      // for fulfillment) and drives which base variant we routed to.
      var designerPath = cfg.designerUrl || '/pages/inlay-ring-designer';
      var designerBase = (window.location.origin + designerPath);
      // Rewrite the authored size (sz) to the PURCHASED size so admin
      // "Edit in Designer" opens the order at the ordered size.
      var designURL = '';
      if (cfg.designHash) {
        var dsp = new URLSearchParams(cfg.designHash.replace(/^#/, ''));
        dsp.set('sz', String(size));
        designURL = designerBase + '#' + dsp.toString();
      }

      var props = {
        '_ring_designer': '1',
        'Material': 'Stainless Steel',
        'Width': cfg.width + 'mm',
        'Size': size,
        '_design_tier': (base.tierSlug || cfg.tierSlug || '').toUpperCase(),
        '_design_url': designURL,
        '_ready_made': '1'
      };
      if (cfg.popularId) props['_popular_id'] = cfg.popularId;
      // Cart line shows the base product's title — carry the DESIGN name so
      // the customer sees what they actually bought (e.g. "Highlander").
      if (cfg.title) props['Design Name'] = String(cfg.title).split(' - ')[0];
      // Cart page + drawer render this exact property name as the line image —
      // without it the cart shows the generic base product, not the design.
      // Fallback to the on-page product image (cache-immune: the img tag is in
      // the same HTML regardless of render age).
      var previewSrc = cfg.previewImage || ((root.querySelector('.rdp-media img') || {}).src || '');
      if (previewSrc) props['Design Preview'] = previewSrc;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: base.variantId, quantity: 1, properties: props })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Add to cart failed'); });
        return r.json();
      }).then(function () {
        // Attribution is durable via the _popular_id line-item property — the admin
        // computes purchases by scanning orders for _popular_id matches, so no
        // separate tracking call is needed here.
        addBtn.textContent = 'Added!';
        msgEl.textContent = '';
        setTimeout(function () { window.location.href = '/cart'; }, 700);
      }).catch(function (err) {
        addBtn.disabled = false;
        addBtn.textContent = orig;
        msgEl.textContent = err.message || 'Failed to add to cart.';
        msgEl.className = 'rdp-msg rdp-msg-err';
      });
    });
  }

  function boot() {
    var roots = document.querySelectorAll('[data-ring-design-product]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
